import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useLanguage } from '../i18n/LanguageContext';
import { languageOptions } from '../i18n/translations';
import { FaGlobeAsia } from 'react-icons/fa';

const SwitchContainer = styled.div`
  position: relative;
  z-index: 1000;
`;

const LanguageButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.1);
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  color: #333;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  svg {
    font-size: 1.2rem;
    color: #0052CC;
  }

  &:hover {
    background: #f8f9fa;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: translateY(${props => props.$isOpen ? '0' : '-10px'});
  transition: all 0.3s ease;
  min-width: 180px;
`;

const MenuItem = styled.button<{ $isActive: boolean }>`
  display: block;
  width: 100%;
  padding: 12px 24px;
  text-align: left;
  background: ${props => props.$isActive ? '#f0f7ff' : 'white'};
  border: none;
  cursor: pointer;
  color: ${props => props.$isActive ? '#0052CC' : '#333'};
  font-weight: ${props => props.$isActive ? '600' : '400'};
  transition: all 0.2s ease;

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }

  &:hover {
    background: #f0f7ff;
  }
`;

const LanguageSwitch: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { language, setLanguage } = useLanguage();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const currentLanguage = languageOptions.find(lang => lang.code === language)?.shortLabel;

  return (
    <SwitchContainer ref={containerRef}>
      <LanguageButton onClick={() => setIsOpen(!isOpen)}>
        <FaGlobeAsia />
        {currentLanguage}
      </LanguageButton>
      <DropdownMenu $isOpen={isOpen}>
        {languageOptions.map(({ code, label }) => (
          <MenuItem
            key={code}
            $isActive={code === language}
            onClick={() => {
              setLanguage(code);
              setIsOpen(false);
            }}
          >
            {label}
          </MenuItem>
        ))}
      </DropdownMenu>
    </SwitchContainer>
  );
};

export default LanguageSwitch;
